import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { MouseEvent, useCallback, useState } from 'react';
import { ChevronRightIcon } from 'lucide-react';
import classnames from 'classnames';

import { UserSubscription } from '@app-types/subscription.type';
import { mapSubscriptionStatus } from '@utils/user-subscription.util';
import SubscriptionStatus from '@constants/subscription.status';
import ThreeDotsIcon from '@icons/three-dots.icon';

import styles from './subscription-row.module.scss';

type Props = {
    subscription: UserSubscription;
    onDetails: (ev: MouseEvent, id: number) => void;
};

const SubscriptionRow = ({ subscription, onDetails }: Props) => {
    const status = mapSubscriptionStatus(subscription.status);
    const billingCycle =
        subscription.billingCycle === 'annually' ? 'anual' : 'mensual';

    const [expanded, setExpanded] = useState(false);

    const toggleExpanded: () => void = useCallback(() => {
        setExpanded(!expanded);
    }, [expanded]);

    const isFamily = subscription.audience === 'family';
    const isMember = subscription.role === 'member';
    // A member's row lists the titular first, then the group's members
    const familyGroup = isFamily
        ? [
              ...(subscription.owner
                  ? [{ ...subscription.owner, isOwner: true }]
                  : []),
              ...(subscription.members ?? []).map((member) => ({
                  ...member,
                  isOwner: false,
              })),
          ]
        : [];
    const hasFamilyGroup = familyGroup.length > 0;

    return (
        <>
            <tr className={styles.row}>
                <td>
                    <div className={styles.serviceNameContainer}>
                        {hasFamilyGroup && (
                            <div
                                className={classnames({
                                    [styles.collapsableIcon]: true,
                                    [styles.expanded]: expanded,
                                })}
                                onClick={toggleExpanded}
                            >
                                <ChevronRightIcon size={18} />
                            </div>
                        )}
                        <span>{subscription.serviceName}</span>
                        <span
                            className={styles.status}
                            style={{
                                color: status.color,
                                backgroundColor: status.background,
                            }}
                        >
                            {status.text}
                        </span>
                    </div>
                </td>
                <td>
                    {subscription.status === SubscriptionStatus.SUSPENDED &&
                        subscription.suspendedAt && (
                            <span>
                                {format(
                                    new Date(subscription.suspendedAt),
                                    "dd 'de' MMMM, yyyy",
                                    { locale: es }
                                )}
                            </span>
                        )}
                </td>
                <td>
                    <span>Facturación {billingCycle}</span>
                </td>
                <td>
                    <span>
                        {format(
                            new Date(subscription.expiresAt),
                            "dd 'de' MMMM, yyyy",
                            {
                                locale: es,
                            }
                        )}
                    </span>
                </td>
                <CompanyCell company={subscription.company} />
                <td style={{ width: '100%' }}>
                    {/* A member's row is the titular's family plan: its actions
                        would act on the whole group, so members get none */}
                    {!isMember && (
                        <div className={styles.moreDetailsContainer}>
                            <div
                                className={styles.moreDetailsIcon}
                                onClick={(ev) => onDetails(ev, subscription.id)}
                            >
                                <ThreeDotsIcon size={16} />
                            </div>
                        </div>
                    )}
                </td>
            </tr>
            {expanded && (
                <div className={styles.relations}>
                    {familyGroup.map((person) => (
                        <div
                            key={`relation-user-${person.id}`}
                            className={styles.relationRow}
                        >
                            <span className={styles.rowPadding} />
                            <a
                                className={styles.link}
                                href={`/users/${person.id}`}
                            >
                                {person.firstName} {person.lastName}
                            </a>
                            {person.isOwner && (
                                <span className={styles.owner}>Titular</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

type CompanyCellProps = {
    company: { id: number; name: string } | null;
};

const CompanyCell = ({ company }: CompanyCellProps) => {
    if (!company) return <td></td>;

    return (
        <td>
            <a className={styles.link} href={`/companies/${company.id}`}>
                {company.name}
            </a>
        </td>
    );
};

export default SubscriptionRow;
